import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TMSService } from '../tms/tms.service';
import { TmsSocketService } from '../tms-socket/tms-socket.service';
import { LocalStorageService } from '../localStorage/local-storage.service';
import { TMS_IDLE_TIMEOUT_MS, TMS_INACTIVITY_OPTION } from '../../constants/tms.const';

/**
 * Detects "no activity on any open tab" and auto-locks the session onto the
 * away-lock screen, the same way a manually selected away option does.
 *
 * Activity is tracked via a single shared localStorage timestamp, so a user
 * active on one tab keeps every other open tab from going idle — but if all
 * tabs sit untouched for TMS_IDLE_TIMEOUT_MS, each tab's own check will fire.
 */
@Injectable({
  providedIn: 'root',
})
export class IdleActivityService {
  private router = inject(Router);
  private tmsService = inject(TMSService);
  private tmsSocketService = inject(TmsSocketService);
  private localStorageService = inject(LocalStorageService);

  private static readonly _LAST_ACTIVITY_KEY = 'tms_last_activity_time';
  private static readonly _CLAIM_KEY = 'tms_idle_claim';
  private static readonly _CHECK_INTERVAL_MS = 5000;
  private static readonly _ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

  private readonly _tabId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  private _checkInterval: any = null;
  private _activityListener: (() => void) | null = null;
  private _lastWriteTime = 0;
  private _isStarted = false;
  private _isTriggering = false;

  start(): void {
    if (this._isStarted) return;
    this._isStarted = true;

    this._recordActivity();

    this._activityListener = () => this._recordActivity();
    IdleActivityService._ACTIVITY_EVENTS.forEach(evt =>
      window.addEventListener(evt, this._activityListener!, { passive: true })
    );

    this._checkInterval = setInterval(() => this._checkIdle(), IdleActivityService._CHECK_INTERVAL_MS);
  }

  stop(): void {
    if (!this._isStarted) return;
    this._isStarted = false;

    if (this._activityListener) {
      IdleActivityService._ACTIVITY_EVENTS.forEach(evt =>
        window.removeEventListener(evt, this._activityListener!)
      );
      this._activityListener = null;
    }
    if (this._checkInterval) {
      clearInterval(this._checkInterval);
      this._checkInterval = null;
    }
  }

  private _recordActivity(): void {
    const now = Date.now();
    // Throttle writes — a raw mousemove listener can fire dozens of times/sec.
    if (now - this._lastWriteTime < 1000) return;
    this._lastWriteTime = now;
    localStorage.setItem(IdleActivityService._LAST_ACTIVITY_KEY, now.toString());
  }

  private _checkIdle(): void {
    if (this._isTriggering) return;
    if (this.tmsSocketService.isLocked) return;
    if (!this.tmsService.dayStarted) return;
    if (this.router.url.includes('/away-lock') || this.router.url.includes('/login')) return;

    const raw = localStorage.getItem(IdleActivityService._LAST_ACTIVITY_KEY);
    const lastActivity = raw ? parseInt(raw, 10) : Date.now();

    if (Date.now() - lastActivity >= TMS_IDLE_TIMEOUT_MS) {
      this._triggerIdleAway();
    }
  }

  private async _triggerIdleAway(): Promise<void> {
    this._isTriggering = true;

    // Cross-tab mutex: several tabs can detect idle on the same tick, so claim
    // the trigger before calling the API and re-check that the claim is still
    // ours (and that no other tab already locked) once it has had time to settle.
    localStorage.setItem(IdleActivityService._CLAIM_KEY, this._tabId);
    await new Promise(resolve => setTimeout(resolve, 150));

    if (this.tmsSocketService.isLocked || localStorage.getItem(IdleActivityService._CLAIM_KEY) !== this._tabId) {
      this._isTriggering = false;
      return;
    }

    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    const breakStartTime = Date.now();

    this.tmsService.breakStart(currentUser?.id).subscribe({
      next: () => {
        localStorage.removeItem(IdleActivityService._CLAIM_KEY);
        this.tmsSocketService.markLocked(breakStartTime);
        this.tmsSocketService.saveAwayOption(TMS_INACTIVITY_OPTION);
        this.tmsSocketService.showLockOverlay();
      },
      error: () => {
        localStorage.removeItem(IdleActivityService._CLAIM_KEY);
        // Leave _isTriggering true only for this tick; allow retry on the next check.
        this._isTriggering = false;
      },
    });
  }
}
